import React, { useEffect } from 'react';
import { seoMeta } from '@/lib/meta';

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  structuredData?: Record<string, any> | null;
  noIndex?: boolean;
}

function upsertMeta(name: string, content: string) {
  if (!content) return;
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertProperty(property: string, content: string) {
  if (!content) return;
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  if (!href) return;
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertAlternate(hreflang: string, href: string) {
  if (!href || !hreflang) return;
  let el = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Make image URL absolute using production domain */
function absoluteImage(img: string): string {
  if (img.startsWith('http')) return img;
  return `${seoMeta.productionUrl}${img}`;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description = seoMeta.description,
  canonical,
  image = seoMeta.ogImage,
  structuredData = null,
  noIndex = false,
}) => {
  useEffect(() => {
    const origin = seoMeta.productionUrl;
    const absImage = absoluteImage(image);

    // Title
    document.title = title.slice(0, 60);

    // Basic meta
    upsertMeta('description', (description || '').slice(0, 160));
    upsertMeta('viewport', seoMeta.viewport);
    upsertMeta('theme-color', seoMeta.themeColor);
    upsertMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('author', seoMeta.founder.name);

    // Canonical — always production domain
    const url = canonical || `${origin}${window.location.pathname}`;
    upsertLink('canonical', url);

    // Open Graph
    upsertProperty('og:title', title);
    upsertProperty('og:description', description || '');
    upsertProperty('og:type', 'website');
    upsertProperty('og:image', absImage);
    upsertProperty('og:image:width', '1200');
    upsertProperty('og:image:height', '630');
    upsertProperty('og:site_name', seoMeta.ogSiteName);
    upsertProperty('og:url', url);
    upsertProperty('og:locale', 'fr_FR');

    // Twitter
    upsertMeta('twitter:card', seoMeta.twitterCard);
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description || '');
    upsertMeta('twitter:image', absImage);

    // Hreflang alternates
    upsertAlternate('fr', url);
    upsertAlternate('x-default', url);

    // Structured data (JSON-LD)
    const scriptId = 'structured-data-jsonld';
    const existing = document.getElementById(scriptId);
    if (structuredData) {
      const script = existing || Object.assign(document.createElement('script'), { id: scriptId });
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(structuredData);
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }
  }, [title, description, canonical, image, structuredData, noIndex]);

  return null;
};
